// src/components/admin/MandatosDefPanel.jsx
import { useEffect, useState } from "react";
import Button from "./Button";
import Select from "./Select";
import TextInput from "./TextInput";
import FieldLabel from "./FieldLabel";
import { API_MAND, fetchJson } from "../Utiles/api";
import { Download, Edit3, Save, X } from "lucide-react";
import { exportRowsToExcel } from "../Utiles/excel";

const MandatosDefPanel = ({ toast, setToast }) => {
  const [codigo, setCodigo] = useState("");
  const [loading, setLoading] = useState(false);
  const [mandatos, setMandatos] = useState([]);

  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({});

  const buscar = async (cod) => {
    const c = String(cod ?? codigo).trim();
    if (!c) {
      setMandatos([]);
      return;
    }
    try {
      setLoading(true);
      const data = await fetchJson(API_MAND.listByColegiado(c));
      setMandatos(Array.isArray(data) ? data : []);
    } catch (e) {
      setToast(e.message || "Error cargando mandatos");
      setMandatos([]);
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (m) => {
    setEditId(m.id);
    setForm({
      mndtId: m.mndtId ?? m.mndt_id ?? "",
      fechaFirma: (m.fechaFirma ?? m.fecha_firma ?? "")?.slice?.(0, 10) || "",
      activo: m.activo === true || String(m.activo) === "true",
    });
  };
  const cancelEdit = () => { setEditId(null); setForm({}); };

  const saveEdit = async (m) => {
    try {
      const body = {
        mndtId: (form.mndtId ?? "").trim() || null,
        fechaFirma: form.fechaFirma ? form.fechaFirma : null,
        activo: !!form.activo,
      };
      await fetchJson(API_MAND.defUpdate(m.id), { method: "PATCH", body: JSON.stringify(body) });
      setToast(`Mandato #${m.id} actualizado`);
      setEditId(null);
      await buscar(m.colegiadoCodigo ?? m.colegiado_codigo ?? codigo);
    } catch (e) {
      setToast(e.message || "Error actualizando mandato");
    }
  };

  const onKeyDownCodigo = (e) => {
    if (e.key === "Enter") buscar();
  };

  return (
    <div className="bg-white rounded-2xl shadow p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Mandatos SEPA Definitivos</h2>
        <Button
          variant="outline"
          iconLeft={<Download size={16} />}
          onClick={() =>
            exportRowsToExcel({
              filename: `mandatos_${codigo || "colegiado"}.xlsx`,
              rows: mandatos,
              columns: [
                { header: "ID", value: (m) => m.id },
                { header: "Colegiado", value: (m) => m.colegiadoCodigo ?? m.colegiado_codigo },
                { header: "mndt_id", value: (m) => m.mndtId ?? m.mndt_id ?? "" },
                {
                  header: "Fecha firma",
                  value: (m) => (m.fechaFirma ?? m.fecha_firma ?? "")?.slice?.(0, 10) || "",
                },
                { header: "Activo", value: (m) => (m.activo === true || String(m.activo) === "true") ? "Sí" : "No" },
                { header: "Creado", value: (m) => m.creadoEl ?? m.creado_el ?? "" },
              ],
            })
          }
        >
          Exportar
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <FieldLabel label="Código colegiado" />
          <div className="flex gap-2">
            <TextInput className="w-40" value={codigo} onChange={(e) => setCodigo(e.target.value)} onKeyDown={onKeyDownCodigo} placeholder="1234" />
            <Button variant="outline" onClick={() => buscar()}>Cargar</Button>
          </div>
        </div>
      </div>

      {!codigo && <div className="text-gray-400 text-sm">Introduce un código de colegiado para ver sus mandatos.</div>}

      {codigo && (
        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                <th>ID</th><th>Colegiado</th><th>mndt_id</th><th>Fecha firma</th><th>Activo</th><th>Creado</th><th></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="py-8 text-center text-gray-500">Cargando…</td></tr>}
              {!loading && mandatos.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-gray-400">Sin mandatos para el colegiado {codigo}</td></tr>}
              {mandatos.map((m) => {
                const editing = editId === m.id;
                return (
                  <tr key={m.id} className="border-t">
                    <td className="py-2">{m.id}</td>
                    <td>{m.colegiadoCodigo ?? m.colegiado_codigo}</td>
                    <td className="font-mono text-xs">
                      {!editing ? (m.mndtId ?? m.mndt_id) : (
                        <TextInput value={form.mndtId} onChange={(e) => setForm((f) => ({ ...f, mndtId: e.target.value }))} className="w-64" />
                      )}
                    </td>
                    <td>
                      {!editing ? (
                        m.fecha_firma ? new Date(m.fecha_firma).toLocaleDateString()
                        : m.fechaFirma ? new Date(m.fechaFirma).toLocaleDateString()
                        : ""
                      ) : (
                        <input type="date" value={form.fechaFirma} onChange={(e) => setForm((f) => ({ ...f, fechaFirma: e.target.value }))} className="rounded-2xl border border-gray-200 px-3 py-2" />
                      )}
                    </td>
                    <td>
                      {!editing ? ((m.activo === true || String(m.activo) === "true") ? "Sí" : "No") : (
                        <input type="checkbox" checked={!!form.activo} onChange={(e) => setForm((f) => ({ ...f, activo: e.target.checked }))} />
                      )}
                    </td>
                    <td>{m.creadoEl || m.creado_el ? new Date(m.creadoEl ?? m.creado_el).toLocaleString() : ""}</td>
                    <td className="flex gap-2">
                      {!editing ? (
                        <Button variant="ghost" iconLeft={<Edit3 size={16} />} onClick={() => startEdit(m)}>Editar</Button>
                      ) : (
                        <>
                          <Button variant="primary" iconLeft={<Save size={16} />} onClick={() => saveEdit(m)}>Guardar</Button>
                          <Button variant="outline" iconLeft={<X size={16} />} onClick={cancelEdit}>Cancelar</Button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MandatosDefPanel;
